import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Search, Building2, Truck, NotebookPen, Trash2, Send, ClipboardList, StickyNote, Coins, Banknote, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const NOTE_KINDS = {
  order: { label: "Sipariş", cls: "bg-teal-500/15 text-teal-300", Icon: ClipboardList },
  tahsilat: { label: "Tahsilat", cls: "bg-emerald-500/15 text-emerald-300", Icon: Coins },
  odeme: { label: "Ödeme", cls: "bg-blue-500/15 text-blue-300", Icon: Banknote },
  avans: { label: "Avans", cls: "bg-amber-500/15 text-amber-300", Icon: AlertTriangle },
  manual: { label: "Not", cls: "bg-slate-500/15 text-slate-300", Icon: StickyNote },
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return (iso || "").slice(0, 16).replace("T", " ");
  }
};

export default function CustomerNotes() {
  const [params, setParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(params.get("contact") || "");
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.contacts().then(setContacts);
    api.noteCounts().then(setCounts).catch(() => {});
  }, []);

  const loadNotes = async (id) => {
    if (!id) { setNotes([]); return; }
    setNotes(await api.contactNotes(id));
  };
  useEffect(() => {
    loadNotes(selectedId);
    if (selectedId) setParams({ contact: selectedId }, { replace: true });
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = contacts.find((c) => c.id === selectedId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = contacts;
    if (q) list = list.filter((c) => (c.name || "").toLowerCase().includes(q));
    return [...list].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0) || (a.name || "").localeCompare(b.name || ""));
  }, [contacts, search, counts]);

  const addNote = async () => {
    const t = text.trim();
    if (!t || !selectedId) return;
    setSaving(true);
    try {
      const n = await api.addContactNote(selectedId, t);
      setNotes((prev) => [n, ...prev]);
      setCounts((prev) => ({ ...prev, [selectedId]: (prev[selectedId] || 0) + 1 }));
      setText("");
      toast.success("Not eklendi");
    } catch {
      toast.error("Not eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (id) => {
    await api.deleteNote(id);
    setNotes((prev) => prev.filter((n) => n.id !== id));
    setCounts((prev) => ({ ...prev, [selectedId]: Math.max(0, (prev[selectedId] || 1) - 1) }));
    toast.success("Not silindi");
  };

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader title="Müşteri Notları" subtitle="Her cari için tarih sıralı notlar · siparişler otomatik kaydolur" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        {/* Contact list */}
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="notes-contact-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari ara..." className="pl-9" />
          </div>
          <div className="max-h-[70vh] space-y-1 overflow-y-auto">
            {filtered.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Cari bulunamadı</div>}
            {filtered.slice(0, 300).map((c) => {
              const Icon = c.type === "customer" ? Building2 : Truck;
              const active = c.id === selectedId;
              return (
                <button
                  key={c.id}
                  data-testid="notes-contact-item"
                  onClick={() => setSelectedId(c.id)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {counts[c.id] ? <span className="shrink-0 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold">{counts[c.id]}</span> : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes timeline */}
        <div className="rounded-xl border border-border bg-card p-5">
          {!selected ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-muted-foreground">
              <NotebookPen className="mb-3 h-10 w-10 opacity-40" />
              Not görüntülemek için soldan bir cari seçin
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center gap-3 border-b border-border pb-4">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${selected.type === "customer" ? "bg-primary/15 text-primary" : "bg-blue-500/15 text-blue-400"}`}>
                  {selected.type === "customer" ? <Building2 className="h-5 w-5" /> : <Truck className="h-5 w-5" />}
                </div>
                <div>
                  <div data-testid="notes-selected-name" className="font-medium">{selected.name}</div>
                  <div className="text-xs text-muted-foreground">{selected.type === "customer" ? "Müşteri" : "Tedarikçi"} · {notes.length} not</div>
                </div>
              </div>

              <div className="mb-5 flex flex-col gap-2 sm:flex-row">
                <Textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Yeni not yazın..." rows={2} className="flex-1 resize-none" onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }} />
                <Button data-testid="note-add-button" onClick={addNote} disabled={saving || !text.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 sm:self-stretch">
                  <Send className="h-4 w-4" /> <span className="ml-1.5">Ekle</span>
                </Button>
              </div>

              <div className="space-y-3">
                {notes.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Henüz not yok</div>}
                {notes.map((n) => {
                  const meta = NOTE_KINDS[n.kind] || NOTE_KINDS.manual;
                  const KindIcon = meta.Icon;
                  return (
                    <div key={n.id} data-testid="note-item" className="group relative rounded-lg border border-border bg-secondary/20 p-3.5">
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>
                          <KindIcon className="h-3 w-3" />
                          {meta.label}
                        </span>
                        <span className="text-[11px] text-muted-foreground">{fmtDate(n.created_at)}</span>
                        <button data-testid="note-delete-button" onClick={() => removeNote(n.id)} className="ml-auto opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-rose-400" /></button>
                      </div>
                      <div className="whitespace-pre-wrap text-sm">{n.text}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
